import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, BadgeCheck, Coffee, ShieldCheck, ArrowRight, Repeat, Settings, PieChart, TrendingUp, TrendingDown, Edit3 } from "lucide-react";

// Billing — recurring Stripe Phase A subscriptions + one-shot tier passes + tip jar.
// Subs surface drives /api/billing/{status,checkout,portal}. Packages drive
// /api/payments/* (legacy one-shot path, retained per No. 6's nod 23 May 2026).

const fmt = (amount, currency) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "GBP").toUpperCase() }).format(amount);

// Tier presentation — labels & blurbs only; price/trial come from /billing/status.
const SUB_TIERS = [
    {
        id: "operator",
        name: "Operator",
        blurb: "The working tier. Unlimited Shredder intake, Drive + Gmail watching, full Ledger and Brief.",
    },
    {
        id: "cooperative",
        name: "Cooperative",
        blurb: "Operator plus team-handler features: shared briefings, multi-agent comms, priority LLM budget.",
    },
];

function SubTierCard({ tier, priceGbp, trialDays, current, onSubscribe, busy }) {
    const isCurrent = current === tier.id;
    return (
        <div
            className={`paper-card p-6 border-2 flex flex-col transition-colors ${isCurrent ? "border-stamp-green" : "border-ink/15 hover:border-stamp-red"}`}
            data-testid={`sub-tier-${tier.id}`}
        >
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <h3 className="font-heading text-xl uppercase tracking-[0.08em] text-ink">{tier.name}</h3>
                <div className="font-heading text-3xl text-stamp-red">
                    £{priceGbp}<span className="text-sm font-body text-ink-soft">/mo</span>
                </div>
            </div>
            <div className="label-typed text-[10px] mb-3">
                {trialDays}-day free trial · cancel anytime
            </div>
            <p className="font-body text-sm text-ink-soft mb-5 flex-1">{tier.blurb}</p>
            {isCurrent ? (
                <div className="font-heading uppercase text-xs tracking-[0.25em] text-stamp-green flex items-center gap-2 justify-center py-3 border-2 border-stamp-green">
                    <BadgeCheck className="w-3 h-3" /> Active
                </div>
            ) : (
                <button
                    onClick={() => onSubscribe(tier.id)}
                    disabled={busy}
                    data-testid={`sub-tier-${tier.id}-buy`}
                    className="font-heading uppercase text-xs tracking-[0.25em] bg-ink text-paper hover:bg-stamp-red px-4 py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Repeat className="w-3 h-3" />}
                    Upgrade to {tier.name}
                </button>
            )}
        </div>
    );
}

function TierCard({ pkg, onSubscribe, busy }) {
    // Iter 31.21: legacy package IDs include a `-30d` suffix (e.g.
    // `tier-operator-30d`). Strip that for the testid so the contract is
    // bare `tier-operator` / `tier-cooperative`, matching the recurring
    // section's `sub-tier-*` naming.
    const bareId = pkg.id.replace(/-\d+d$/, "");
    return (
        <div
            className="paper-card p-6 border-2 border-ink/15 hover:border-stamp-red transition-colors flex flex-col"
            data-testid={`tier-${bareId}`}
        >
            <div className="flex items-baseline justify-between gap-2 mb-3">
                <h3 className="font-heading text-xl uppercase tracking-[0.08em] text-ink">
                    {pkg.label.split("·")[0].trim()}
                </h3>
                <div className="font-heading text-3xl text-stamp-red">{fmt(pkg.amount, pkg.currency)}</div>
            </div>
            <div className="label-typed text-[10px] mb-3">{pkg.duration_days} days · renewable</div>
            <p className="font-body text-sm text-ink-soft mb-5 flex-1">{pkg.blurb}</p>
            <button
                onClick={() => onSubscribe(pkg.id)}
                disabled={busy}
                data-testid={`tier-${bareId}-buy`}
                className="font-heading uppercase text-xs tracking-[0.25em] bg-ink text-paper hover:bg-stamp-red px-4 py-3 disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                Subscribe
            </button>
        </div>
    );
}

function TipPill({ pkg, onTip, busy }) {
    return (
        <button
            onClick={() => onTip(pkg.id)}
            disabled={busy}
            data-testid={`tip-${pkg.id}-buy`}
            className="paper-card border-2 border-dashed border-ink/30 hover:border-stamp-red px-5 py-4 disabled:opacity-50 flex items-center gap-3"
        >
            <Coffee className="w-4 h-4 text-stamp-red" />
            <div>
                <div className="font-heading text-base text-ink">{fmt(pkg.amount, pkg.currency)}</div>
                <div className="label-typed text-[9px]">one-time</div>
            </div>
        </button>
    );
}


// Iter 31.49 — Credits section. Live balance, top-up tiers, recent ledger.
function CreditsSection() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/credits/balance");
            setData(data);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        }
    };

    useEffect(() => {
        load();
        const h = () => load();
        window.addEventListener("shed:credits-changed", h);
        return () => window.removeEventListener("shed:credits-changed", h);
    }, []);

    const topup = async (tier) => {
        setBusy(tier); setErr("");
        try {
            const { data } = await api.post("/credits/topup-checkout", { tier });
            if (data?.url) window.location.assign(data.url);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(""); }
    };

    // Iter 31.85 — Claim welcome credits manually. Safety net for
    // operators whose auto-grant on first sign-in didn't fire (e.g.
    // Google-auth users who signed in before iter 31.75 landed).
    // Idempotent on the server via the welcome:<user_id> ref_id, so
    // a second click on a balance>0 account just returns duplicate=true.
    const claimWelcome = async () => {
        setBusy("welcome"); setErr("");
        try {
            const { data: out } = await api.post("/credits/claim-welcome");
            // Iter 31.90 — if the server says "duplicate" but balance is
            // still zero, the cache desynced from the ledger (Six's prod
            // bug on emes.m.andrews@gmail.com). Auto-fire reconcile to
            // rebuild the cache from the append-only ledger.
            if (out?.duplicate && (out?.balance ?? 0) === 0) {
                try {
                    const { data: rec } = await api.post("/credits/reconcile-balance");
                    if (rec?.repaired) {
                        setErr(`Cache desync repaired from ledger — balance restored to ${rec.new_balance}.`);
                    } else {
                        setErr("Welcome bundle already claimed — balance is current.");
                    }
                } catch (e2) {
                    setErr(`Reconcile failed: [HTTP ${e2?.response?.status || "?"}] ${e2?.response?.data?.detail || e2.message}`);
                }
            } else if (out?.duplicate) {
                setErr("Welcome bundle already claimed — balance is current.");
            }
            await load();
            window.dispatchEvent(new Event("shed:credits-changed"));
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(""); }
    };

    if (!data) return null;

    return (
        <div className="mb-10" data-testid="credits-section">
            <h2 className="font-heading text-lg uppercase tracking-[0.22em] text-ink mb-2">
                Credits · pay per action
            </h2>
            <p className="font-body text-sm text-ink-soft mb-4 max-w-2xl">
                Credits power the LLM-driven actions in The Shed. Subscriptions grant a
                monthly bundle; top-ups add discretionary credit. Free actions (Shred
                intake, routing-rule compile) stay free regardless.
            </p>

            <div className="paper-card p-5 mb-4 border-2 border-stamp-red/40 flex items-baseline justify-between gap-4 flex-wrap" data-testid="credits-balance-card">
                <div>
                    <div className="label-typed tw-prefix">Live balance</div>
                    <div className="font-heading text-5xl text-stamp-red mt-1" data-testid="credits-balance">
                        {data.balance}
                        <span className="text-sm font-body text-ink-soft ml-2">credits</span>
                    </div>
                </div>
                <div className="font-body text-xs text-ink-soft">
                    <div>Brief · <span className="font-heading text-ink">{data.pricing.brief} credit</span></div>
                    <div>Cold Read · <span className="font-heading text-ink">{data.pricing.cold_read} credits</span></div>
                    <div>CV compose · <span className="font-heading text-ink">{data.pricing.cv_compose} credits</span></div>
                    <div>Shred · <span className="font-heading text-stamp-green">free</span></div>
                </div>
            </div>

            {/* Iter 31.85 — Claim Welcome button. Only shows when balance is
                zero. Idempotent server-side, so an extra click on a
                higher-balance account is harmless — but hiding it on
                non-zero balances keeps the UI honest. */}
            {data.balance === 0 && (
                <div className="paper-card p-4 mb-4 border border-stamp-red bg-stamp-red/5" data-testid="claim-welcome-card">
                    <div className="font-body text-sm text-ink mb-3">
                        <span className="font-heading uppercase tracking-[0.18em] text-stamp-red text-xs mr-2">
                            One-time offer
                        </span>
                        100 credits on us (£10). Spend on Briefs, Cold Reads, CV registers,
                        agent emulations. New operators get this automatically — if you
                        signed in but never saw it, claim it here.
                    </div>
                    <button
                        onClick={claimWelcome}
                        disabled={busy === "welcome"}
                        data-testid="claim-welcome-btn"
                        className="font-heading uppercase text-[10px] tracking-[0.25em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy === "welcome" ? "Claiming…" : "Claim 100 welcome credits"}
                    </button>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-3 mb-6">
                {Object.entries(data.topup_tiers).map(([tier, cfg]) => (
                    <button
                        key={tier}
                        onClick={() => topup(tier)}
                        disabled={!!busy}
                        data-testid={`credits-topup-${tier}`}
                        className="paper-card p-5 border-2 border-ink/15 hover:border-stamp-red text-left transition-colors disabled:opacity-50"
                    >
                        <div className="font-heading text-2xl text-ink">£{tier}</div>
                        <div className="font-heading text-stamp-red text-3xl mt-1">
                            {cfg.credits}
                            <span className="text-xs font-body text-ink-soft ml-2">credits</span>
                        </div>
                        <div className="label-typed text-[10px] mt-2">
                            {tier === "5" ? "starter" : tier === "10" ? "+10% bonus" : "+20% bonus"}
                        </div>
                        {busy === tier && (
                            <div className="flex items-center gap-2 mt-3 font-heading uppercase text-[10px] tracking-[0.22em] text-stamp-red">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Redirecting to Stripe…
                            </div>
                        )}
                    </button>
                ))}
            </div>

            {err && (
                <div className="paper-card border border-stamp-red/40 p-3 mb-4" data-testid="credits-error">
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                </div>
            )}

            {data.recent && data.recent.length > 0 && (
                <details className="paper-card p-4 mb-4" data-testid="credits-recent">
                    <summary className="font-heading uppercase text-[11px] tracking-[0.22em] text-ink-soft cursor-pointer hover:text-stamp-red">
                        Recent activity · last {data.recent.length}
                    </summary>
                    <ul className="mt-3 space-y-1">
                        {data.recent.map((r) => (
                            <li key={r.id} className="font-body text-xs flex items-baseline justify-between gap-3">
                                <span className="text-ink-soft">{r.ts?.slice(0, 16).replace("T", " ")}</span>
                                <span className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">
                                    {r.kind}
                                </span>
                                <span className="flex-1 truncate text-ink">{r.action || r.note || ""}</span>
                                <span className={`font-heading ${r.amount > 0 ? "text-stamp-green" : "text-stamp-red"}`}>
                                    {r.amount > 0 ? `+${r.amount}` : r.amount}
                                </span>
                                <span className="font-body text-[10px] text-ink-faint w-12 text-right">
                                    bal {r.balance_after}
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}


// Iter 31.50 — Fractional Accounts. Pure view layer over /api/credits/* — the
// existing wallet, segmented by canon label (Spy Finance / Gary's Shed /
// Agent 52). Two can relabel any single transaction; the underlying ledger
// row is never altered (Canon Rule 1). The override is stored as metadata.
const FRAC_LABELS = ["Spy Finance", "Gary's Shed", "Agent 52"];
const LABEL_THEME = {
    "Spy Finance": { tone: "text-stamp-green", chip: "bg-stamp-green/15 text-stamp-green", icon: TrendingUp },
    "Gary's Shed": { tone: "text-stamp-red",   chip: "bg-stamp-red/15 text-stamp-red",     icon: TrendingDown },
    "Agent 52":    { tone: "text-ink",         chip: "bg-ink/10 text-ink",                  icon: PieChart },
};

function fmtRunway(days) {
    if (days == null) return "∞";
    if (days >= 730) return `${Math.round(days / 365)}y`;
    if (days >= 60) return `${Math.round(days / 30)}mo`;
    return `${days}d`;
}

function FractionalSection() {
    const [summary, setSummary] = useState(null);
    const [activeLabel, setActiveLabel] = useState(null);  // null = all
    const [rows, setRows] = useState([]);
    const [loadingRows, setLoadingRows] = useState(false);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null);  // row_id currently being relabelled
    const [busyRow, setBusyRow] = useState("");
    const [windowDays, setWindowDays] = useState(30);

    const loadSummary = async (days = windowDays) => {
        try {
            const { data } = await api.get(`/fractional/summary?days=${days}`);
            setSummary(data);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        }
    };

    const loadRows = async (label = activeLabel) => {
        setLoadingRows(true); setErr("");
        try {
            const q = label ? `?label=${encodeURIComponent(label)}&limit=100` : "?limit=100";
            const { data } = await api.get(`/fractional/transactions${q}`);
            setRows(data.entries || []);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally {
            setLoadingRows(false);
        }
    };

    useEffect(() => { loadSummary(); loadRows(); /* eslint-disable-next-line */ }, []);

    const filterTo = (label) => {
        setActiveLabel(label);
        loadRows(label);
    };

    const setRowLabel = async (rowId, label) => {
        setBusyRow(rowId); setErr("");
        try {
            await api.post(`/fractional/label/${rowId}`, { label, reason: "operator relabel" });
            setEditing(null);
            await loadSummary();
            await loadRows();
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusyRow("");
        }
    };

    if (!summary) return null;

    const trajectoryChip = summary.trajectory === "positive"
        ? "bg-stamp-green/15 text-stamp-green"
        : summary.trajectory === "negative"
            ? "bg-stamp-red/15 text-stamp-red"
            : "bg-ink/10 text-ink";

    return (
        <div className="mb-12" data-testid="fractional-section">
            <h2 className="font-heading text-lg uppercase tracking-[0.22em] text-ink mb-2">
                Fractional accounts · wallet view
            </h2>
            <p className="font-body text-sm text-ink-soft mb-4 max-w-2xl">
                The wallet, segmented. Spy Finance is revenue (subscriptions + top-ups);
                Gary's Shed is operating burn (LLM actions); Agent 52 is internal grants.
                Two can relabel any single transaction — the underlying ledger row stays
                canon (Rule 1). Window:{" "}
                {[7, 30, 90, 365].map((d) => (
                    <button
                        key={d}
                        onClick={() => { setWindowDays(d); loadSummary(d); }}
                        data-testid={`frac-window-${d}d`}
                        className={`ml-2 font-heading uppercase tracking-[0.18em] text-[10px] px-2 py-0.5 border ${windowDays === d ? "border-stamp-red text-stamp-red" : "border-ink/20 text-ink-soft hover:border-stamp-red"}`}
                    >
                        {d}d
                    </button>
                ))}
            </p>

            {/* Trajectory headline */}
            <div className="paper-card p-5 mb-4 border-2 border-ink/15" data-testid="frac-headline">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <div className="label-typed tw-prefix">Revenue · {windowDays}d</div>
                        <div className="font-heading text-3xl text-stamp-green mt-1" data-testid="frac-revenue">
                            £{summary.revenue_gbp_window.toFixed(2)}
                        </div>
                        <div className="font-body text-[10px] text-ink-faint">{summary.revenue_credits_window} credits</div>
                    </div>
                    <div>
                        <div className="label-typed tw-prefix">Burn · {windowDays}d</div>
                        <div className="font-heading text-3xl text-stamp-red mt-1" data-testid="frac-burn">
                            £{summary.burn_gbp_window.toFixed(2)}
                        </div>
                        <div className="font-body text-[10px] text-ink-faint">{summary.burn_credits_window} credits</div>
                    </div>
                    <div>
                        <div className="label-typed tw-prefix">Net · {windowDays}d</div>
                        <div className={`font-heading text-3xl mt-1 ${summary.net_credits_window >= 0 ? "text-stamp-green" : "text-stamp-red"}`} data-testid="frac-net">
                            {summary.net_credits_window >= 0 ? "+" : ""}{summary.net_credits_window}
                        </div>
                        <span className={`label-typed text-[9px] px-1.5 py-0.5 ${trajectoryChip}`} data-testid="frac-trajectory">
                            {summary.trajectory}
                        </span>
                    </div>
                    <div>
                        <div className="label-typed tw-prefix">Runway</div>
                        <div className="font-heading text-3xl text-ink mt-1" data-testid="frac-runway">
                            {fmtRunway(summary.runway_days)}
                        </div>
                        <div className="font-body text-[10px] text-ink-faint">
                            {summary.daily_burn_credits.toFixed(2)} cr/day · bal £{summary.balance_gbp.toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* Iter 31.52 — Projection strip */}
                {summary.projections && (
                    <div className="mt-4 pt-4 border-t border-ink/10" data-testid="frac-projections">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="label-typed tw-prefix">Projected runway · by burn window</div>
                            <span
                                className={`label-typed text-[10px] px-2 py-0.5 ${
                                    summary.drift === "accelerating" ? "bg-stamp-red/15 text-stamp-red" :
                                    summary.drift === "slowing" ? "bg-stamp-green/15 text-stamp-green" :
                                    "bg-ink/10 text-ink"
                                }`}
                                data-testid="frac-drift"
                                title="7d daily burn vs 30d daily burn. Accelerating = burn rate is rising."
                            >
                                drift · {summary.drift}
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {summary.projections.map((p) => (
                                <div
                                    key={p.window_days}
                                    data-testid={`frac-projection-${p.window_days}d`}
                                    className="paper-card p-3 border border-ink/15"
                                >
                                    <div className="label-typed text-[10px] text-ink-soft">{p.window_days}d burn avg</div>
                                    <div className="font-heading text-xl text-ink mt-1">
                                        {fmtRunway(p.projected_runway_days)}
                                        <span className="text-[10px] font-body text-ink-faint ml-2">runway</span>
                                    </div>
                                    <div className="font-body text-[10px] text-ink-faint">
                                        {p.daily_burn_credits.toFixed(2)} cr/day · £{p.burn_gbp.toFixed(2)}/{p.window_days}d
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Segment cards */}
            <div className="grid md:grid-cols-3 gap-3 mb-6">
                {summary.segments.map((seg) => {
                    const theme = LABEL_THEME[seg.label] || LABEL_THEME["Agent 52"];
                    const Icon = theme.icon;
                    const isActive = activeLabel === seg.label;
                    return (
                        <button
                            key={seg.label}
                            onClick={() => filterTo(isActive ? null : seg.label)}
                            data-testid={`frac-segment-${seg.label.replace(/[^a-z]/gi, "").toLowerCase()}`}
                            className={`paper-card p-4 border-2 text-left transition-colors ${isActive ? "border-stamp-red" : "border-ink/15 hover:border-ink/40"}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Icon className={`w-4 h-4 ${theme.tone}`} />
                                <div className="font-heading uppercase tracking-[0.18em] text-xs text-ink">
                                    {seg.label}
                                </div>
                            </div>
                            <div className="font-heading text-2xl text-ink">
                                {seg.credits_in_window - seg.credits_out_window >= 0 ? "+" : ""}
                                {seg.credits_in_window - seg.credits_out_window}
                                <span className="text-xs font-body text-ink-soft ml-1">net</span>
                            </div>
                            <div className="font-body text-[11px] text-ink-soft mt-1 flex gap-3">
                                <span><span className="text-stamp-green">+{seg.credits_in_window}</span> in</span>
                                <span><span className="text-stamp-red">-{seg.credits_out_window}</span> out</span>
                                <span className="text-ink-faint">{seg.entry_count_window} rows</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {err && (
                <div className="paper-card border border-stamp-red/40 p-3 mb-4" data-testid="frac-error">
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                </div>
            )}

            {/* Transaction table */}
            <div className="paper-card p-4" data-testid="frac-transactions">
                <div className="flex items-baseline justify-between mb-3">
                    <div className="font-heading uppercase text-[11px] tracking-[0.22em] text-ink-soft">
                        Transactions {activeLabel ? `· filtered to ${activeLabel}` : "· all labels"}
                    </div>
                    {activeLabel && (
                        <button
                            onClick={() => filterTo(null)}
                            data-testid="frac-clear-filter"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] text-stamp-red hover:underline"
                        >
                            clear filter
                        </button>
                    )}
                </div>
                {loadingRows ? (
                    <div className="flex items-center gap-2 py-6 text-ink-soft">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span className="font-body text-xs">Loading transactions…</span>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="font-body text-xs text-ink-faint py-6 text-center">
                        No transactions in this view.
                    </div>
                ) : (
                    <ul className="space-y-1.5">
                        {rows.map((r) => {
                            const theme = LABEL_THEME[r.resolved_label] || LABEL_THEME["Agent 52"];
                            const isEditing = editing === r.id;
                            return (
                                <li key={r.id} className="flex items-baseline gap-3 text-xs font-body py-1.5 border-b border-ink/5 last:border-0">
                                    <span className="text-ink-faint w-28 shrink-0">{r.ts?.slice(0, 16).replace("T", " ")}</span>
                                    <span className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft w-24 shrink-0">
                                        {r.kind}
                                    </span>
                                    <span className="flex-1 truncate text-ink min-w-0">
                                        {r.action || r.note || r.ref_id || r.id.slice(0, 8)}
                                    </span>
                                    <span className={`font-heading ${r.amount > 0 ? "text-stamp-green" : "text-stamp-red"} w-16 text-right shrink-0`}>
                                        {r.amount > 0 ? `+${r.amount}` : r.amount}
                                    </span>
                                    {isEditing ? (
                                        <div className="flex gap-1">
                                            {FRAC_LABELS.map((l) => (
                                                <button
                                                    key={l}
                                                    onClick={() => setRowLabel(r.id, l)}
                                                    disabled={busyRow === r.id}
                                                    data-testid={`frac-relabel-${r.id}-${l.replace(/[^a-z]/gi, "").toLowerCase()}`}
                                                    className={`label-typed text-[9px] px-1.5 py-0.5 border hover:border-stamp-red ${r.resolved_label === l ? "border-stamp-red text-stamp-red" : "border-ink/20 text-ink-soft"}`}
                                                >
                                                    {l}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => setEditing(null)}
                                                className="label-typed text-[9px] px-1 py-0.5 text-ink-faint hover:text-ink"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setEditing(r.id)}
                                            data-testid={`frac-edit-${r.id}`}
                                            className={`label-typed text-[9px] px-1.5 py-0.5 border border-transparent hover:border-stamp-red flex items-center gap-1 ${theme.chip}`}
                                            title={r.label_overridden ? "Override applied · click to change" : "Default label · click to override"}
                                        >
                                            {r.resolved_label}
                                            {r.label_overridden && <span className="text-[8px]">*</span>}
                                            <Edit3 className="w-2.5 h-2.5 opacity-50" />
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}


export default function Billing() {
    const [packages, setPackages] = useState(null);
    const [entitlements, setEntitlements] = useState(null);
    const [subStatus, setSubStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [subBusy, setSubBusy] = useState(false);
    const [portalBusy, setPortalBusy] = useState(false);
    const [err, setErr] = useState("");

    const loadSubStatus = async () => {
        try {
            const { data } = await api.get("/billing/status");
            setSubStatus(data);
        } catch (e) {
            // 401/503 acceptable — page still renders one-shot UI below.
            setSubStatus({ configured: false });
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/payments/packages");
                setPackages(data);
            } catch (e) {
                setErr(`Could not load packages: ${e.message}`);
            }
            try {
                const { data: ent } = await api.get("/payments/me/entitlements");
                setEntitlements(ent);
            } catch (_) {
                // not signed in for tier display — fine
            }
            await loadSubStatus();
        })();
    }, []);

    const checkout = async (pkgId) => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/payments/checkout/create", {
                package_id: pkgId,
                origin_url: window.location.origin,
            });
            if (!data?.url) throw new Error("Stripe returned no checkout URL");
            window.location.href = data.url;
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setErr(`[HTTP ${e.response?.status || "?"}] ${detail}`);
            setBusy(false);
        }
    };

    const subscribe = async (tier) => {
        setSubBusy(true); setErr("");
        try {
            const { data } = await api.post("/billing/checkout", { tier });
            if (!data?.url) throw new Error("Stripe returned no checkout URL");
            window.location.href = data.url;
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setErr(`[HTTP ${e.response?.status || "?"}] ${detail}`);
            setSubBusy(false);
        }
    };

    const openPortal = async () => {
        setPortalBusy(true); setErr("");
        try {
            const { data } = await api.post("/billing/portal");
            if (!data?.url) throw new Error("Stripe returned no portal URL");
            window.location.href = data.url;
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setErr(`[HTTP ${e.response?.status || "?"}] ${detail}`);
            setPortalBusy(false);
        }
    };

    const subTiers = subStatus?.tiers || {};
    const activeTier = subStatus?.tier && subStatus.tier !== "free" ? subStatus.tier : null;
    const hasCustomer = Boolean(subStatus?.stripe_customer_id);
    const inTrial = subStatus?.status === "trialing";

    return (
        <Shell>
            <div className="mb-8">
                <div className="label-typed tw-prefix">Cashier · entity scope · spyfinance.cloud</div>
                <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                    Billing<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Tier passes buy 30 days at a time. Renewable, not auto-billed.
                    Tips support the brand on Gary's Shed surface. Test mode is in
                    effect — use card <span className="font-heading tracking-[0.15em] text-stamp-red">4242 4242 4242 4242</span>,
                    any future expiry, any CVC.
                </p>
            </div>

            {packages?.stripe_mode === "test" && (
                <div className="paper-card border border-stamp-red/40 p-3 mb-6 flex items-center gap-3" data-testid="billing-test-banner">
                    <ShieldCheck className="w-4 h-4 text-stamp-red" />
                    <div className="font-heading uppercase tracking-[0.2em] text-[10px] text-stamp-red">
                        Test mode · no real money will move
                    </div>
                    <div className="label-typed text-[9px] hidden md:block">{packages.test_card}</div>
                </div>
            )}

            {entitlements?.active?.length > 0 && (
                <div className="paper-card border-2 border-stamp-green p-4 mb-8" data-testid="billing-current-tier">
                    <div className="flex items-center gap-3 flex-wrap">
                        <BadgeCheck className="w-4 h-4 text-stamp-green" />
                        <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                            Active tier · {entitlements.active[0].tier}
                        </div>
                        <span className="label-typed text-[10px]">
                            {entitlements.active[0].remaining_days} days remaining
                        </span>
                    </div>
                </div>
            )}

            {err && (
                <div className="paper-card border-2 border-stamp-red p-3 mb-6" data-testid="billing-error">
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                </div>
            )}

            {/* Credits — iter 31.49 */}
            <CreditsSection />

            {/* Fractional accounts — iter 31.50 */}
            <FractionalSection />

            {/* Recurring subscriptions — Stripe Phase A */}
            <h2 className="font-heading text-lg uppercase tracking-[0.22em] text-ink mb-2">
                Recurring tier · Spy Finance
            </h2>
            <p className="font-body text-sm text-ink-soft mb-4 max-w-2xl">
                Monthly subscription. Seven-day free trial on both tiers — card collected, not charged
                until day 8. Cancel through the customer portal at any time before then with no charge.
            </p>

            {subStatus && !subStatus.configured && (
                <div className="paper-card border border-stamp-red/40 p-3 mb-4" data-testid="sub-not-configured">
                    <div className="label-typed text-[10px] text-stamp-red">
                        Stripe not configured on this deployment. Subscriptions unavailable.
                    </div>
                </div>
            )}

            {activeTier && (
                <div className="paper-card border-2 border-stamp-green p-4 mb-4" data-testid="sub-active-banner">
                    <div className="flex items-center gap-3 flex-wrap">
                        <BadgeCheck className="w-4 h-4 text-stamp-green" />
                        <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                            Subscribed · {activeTier}
                        </div>
                        {inTrial && subStatus.trial_end && (
                            <span className="label-typed text-[10px] text-stamp-red">
                                Trial ends {new Date(subStatus.trial_end).toLocaleDateString()}
                            </span>
                        )}
                        {!inTrial && subStatus.current_period_end && (
                            <span className="label-typed text-[10px]">
                                Renews {new Date(subStatus.current_period_end).toLocaleDateString()}
                            </span>
                        )}
                        {subStatus.cancel_at_period_end && (
                            <span className="label-typed text-[10px] text-stamp-red">Will not renew</span>
                        )}
                    </div>
                </div>
            )}

            {subStatus?.configured && (
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    {SUB_TIERS.map((tier) => (
                        <SubTierCard
                            key={tier.id}
                            tier={tier}
                            priceGbp={subTiers[tier.id]?.price_gbp || "—"}
                            trialDays={subTiers[tier.id]?.trial_days || 7}
                            current={activeTier}
                            onSubscribe={subscribe}
                            busy={subBusy}
                        />
                    ))}
                </div>
            )}

            {hasCustomer && (
                <div className="mb-12 flex flex-wrap items-center gap-3">
                    <button
                        onClick={openPortal}
                        disabled={portalBusy}
                        data-testid="sub-manage-button"
                        className="font-heading uppercase text-xs tracking-[0.22em] px-4 py-3 border-2 border-ink text-ink hover:bg-ink hover:text-paper disabled:opacity-50 flex items-center gap-2"
                    >
                        {portalBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
                        Manage subscription
                    </button>
                    <span className="label-typed text-[10px]">
                        Update card · cancel · view invoices via Stripe Customer Portal
                    </span>
                </div>
            )}

            <div className="hairline mb-8" />

            {/* Subscription tiers */}
            <h2 className="font-heading text-lg uppercase tracking-[0.22em] text-ink mb-4">
                Tier passes · Spy Finance
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-12">
                {packages?.subscriptions?.map((pkg) => (
                    <TierCard key={pkg.id} pkg={pkg} onSubscribe={checkout} busy={busy} />
                ))}
            </div>

            <div className="hairline mb-8" />

            {/* Tips */}
            <h2 className="font-heading text-lg uppercase tracking-[0.22em] text-ink mb-2">
                Support the brand · Gary's Shed
            </h2>
            <p className="font-body text-sm text-ink-soft mb-4">
                One-time donations. Anonymous-friendly. Goes toward LLM credits, hosting,
                and the patterns staying free.
            </p>
            <div className="flex flex-wrap gap-3 mb-12">
                {packages?.tips?.map((pkg) => (
                    <TipPill key={pkg.id} pkg={pkg} onTip={checkout} busy={busy} />
                ))}
            </div>

            <div className="label-typed text-[9px] italic text-center">
                Test mode · Stripe checkout · session results polled on return
            </div>
        </Shell>
    );
}
